import * as z from 'zod';

export const passwordSchema = z
  .string({ message: 'Password is required' })
  .min(8, 'Password must be at least 8 characters long')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(
    /[^A-Za-z0-9]/,
    'Password must contain at least one special character',
  );

export const phoneNumberSchema = z
  .string()
  .min(7, 'Phone number must be at least 7 digits')
  .transform((val) => val.replace(/[^0-9]/g, ''));

export function optional<T>(
  schema: z.ZodType<T>,
): z.ZodType<T | undefined | null> {
  return schema.nullable().optional();
}

const errorMapMessage = (message: string) => {
  return { message };
};

export const requiredString = (name: string): z.ZodString => {
  const errorMessage = `${name} is required`;
  return z
    .string({
      errorMap: () => errorMapMessage(errorMessage),
    })
    .min(1, errorMessage);
};

export const requiredNumber = (name: string): z.ZodNumber => {
  const errorMessage = `${name} is required`;
  return z.coerce
    .number({
      errorMap: () => errorMapMessage(errorMessage),
    })
    .min(1, `${name} must be greater than 0`);
};

export function getZodEnum<T extends string>(
  data: T[],
): z.ZodEnum<[T, ...T[]]> {
  return z.enum([data[0], ...data.slice(1)], {
    invalid_type_error: 'Invalid value',
  });
}

export const domainOrWebpageSchema = z
  .string()
  .refine(
    (value) => {
      // Domain validation: no protocol, no www, contains a dot
      const isDomain = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(
        value,
      );
      // Webpage URL validation: starts with https:// or www.
      const isWebpageUrl = /^(https:\/\/|www\.)/.test(value);
      return isDomain || isWebpageUrl;
    },
    {
      message:
        'Invalid input. Domain should be without protocol/www, webpage URL should start with https:// or www.',
    },
  )
  .transform((value) => {
    // If it's a webpage URL, extract hostname
    if (/^(https:\/\/|www\.)/.test(value)) {
      const urlObject = new URL(
        value.startsWith('www.') ? `https://${value}` : value,
      );
      return urlObject.hostname.replace(/^www\./, '');
    }
    // If it's already a clean domain, return as-is
    return value;
  });

export const domainOrURLSchema = z
  .string()
  .regex(/^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+)/, {
    message: 'Invalid URL or hostname',
  })
  .transform((url) => url.replace(/^(https?:\/\/)?(www\.)?/, ''));
